<script setup lang="ts">
import { Color } from '@previo/codebooks'
import { computed, type PropType } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { useDateTimePicker } from '../composables/DateTimePickerState'
import { applyDefaultTime } from '../../date-picker/utils'
import PTextFormBody from '../../typography/form/PTextFormBody.vue'

const props = defineProps({
  date: {
    type: Object as PropType<Dayjs | null>,
    default: null,
  },
  dim: {
    type: Boolean,
    default: false,
  },
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
})

type Emits = {
  (e: 'select'): void
}

const emits = defineEmits<Emits>()

const model = defineModel<Dayjs | null>()

const navigation = useDateTimePicker()

const color = computed<Color>(() => {
  if (isSelected.value) {
    return Color.WHITE
  }

  if (props.dim || isDisabled.value) {
    return Color.GREY_300
  }

  return Color.GREY_500
})

const isSelected = computed<boolean>(() => {
  if (!props.date) {
    return false
  }

  return model.value?.tz().isSame(props.date, 'date') || false
})

const isToday = computed<boolean>(() => {
  if (!props.date) {
    return false
  }

  return props.date.isTzToday()
})

const isDisabled = computed<boolean>(() => {
  if (!props.date) {
    return true
  }

  const min = navigation.getMin()
  const max = navigation.getMax()

  return (!!min && props.date.isBefore(min, 'date')) || (!!max && props.date.isAfter(max, 'date'))
})

const onClick = (): void => {
  if (!props.date || isDisabled.value) {
    return
  }

  if (model.value?.isSame(props.date, 'date')) {
    if (!navigation.isDisableClearable()) {
      model.value = null
      return
    }

    emits('select')
    return
  }

  model.value = model.value
    ? props.date.withTimeFrom(model.value)
    : applyDefaultTime(props.date, props.defaultTime)
  navigation.setDate(props.date)
  emits('select')
}
</script>

<template>
  <div
    class="ds-date-time-picker__date-cell-wrapper p-flex p-flex-center p-non-selectable p-full-width"
  >
    <div>
      <div
        class="p-cursor-pointer"
        :class="[
          'ds-date-time-picker__date-cell p-flex p-flex-center p-items-center',
          {
            'ds-date-time-picker__date-cell--selected': isSelected,
            'ds-date-time-picker__date-cell--disabled': isDisabled,
            'ds-date-time-picker__date-cell--today': isToday,
          },
        ]"
        @click="onClick"
      >
        <p-text-form-body :text="date?.format('D') || ''" :color="color" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../assets/scss/shared/variables/ds.radius';
@import '../../../assets/scss/shared/ds.variables.module';
@import '../../../assets/scss/shared/brand.module';
@import '../../../assets/scss/shared/mixins';

$mobile-cell-height: 42px;
$laptop-cell-height: 36px;

.ds-date-time-picker {
  &__date-cell-wrapper {
    height: $laptop-cell-height;

    @include mobile {
      height: $mobile-cell-height;
      width: $mobile-cell-height;
    }
  }

  &__date-cell {
    height: $laptop-cell-height;
    width: $laptop-cell-height;

    @include mobile {
      height: $mobile-cell-height;
      width: 100%;
      aspect-ratio: 1;
    }
    border-radius: $global-border-radius;

    &--selected {
      background-color: getColor('primary') !important;
    }

    &--today:not(&--selected) {
      background-color: getColor('note') !important;
    }

    &:hover:not(&--disabled) {
      background-color: getColor('grey100');
    }

    &--disabled {
      pointer-events: none;
    }
  }
}
</style>
